import { ComponentType, lazy, ReactNode } from 'react';
import { logger } from '@/lib/logger';

interface LazyRetryOptions {
  maxRetries?: number;
  delay?: number;
  timeout?: number;
}

/**
 * Enhanced lazy loader with retry logic, timeout, and better error handling
 * PERFORMANCE: Reduced default retries and faster timeouts for better UX
 */
export function lazyWithRetry<T extends ComponentType<Record<string, unknown>>>(
  componentImport: () => Promise<{ default: T }>,
  options: LazyRetryOptions = {}
): React.LazyExoticComponent<T> {
  const { maxRetries = 3, delay = 300, timeout = 15000 } = options; // 15s: heavy chunks (Stream chat/video on Messages) exceed a 5s budget on slow links

  return lazy(async () => {
    let lastError: Error | undefined;
    const startTime = performance.now();

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        // Clear HMR cache in development to force fresh module load
        if (import.meta.hot && attempt > 0) {
          try {
            import.meta.hot.invalidate();
          } catch (e) {
            // Ignore HMR errors
          }
        }

        // Add timeout to prevent hanging
        const module = await Promise.race([
          componentImport(),
          new Promise<never>((_, reject) => {
            setTimeout(() => reject(new Error('Component load timeout')), timeout);
          })
        ]);

        // Verify module has default export
        if (!module || typeof module !== 'object' || !('default' in module)) {
          throw new Error('Module does not have a default export');
        }

        // Log slow loads in development
        if (import.meta.env.DEV) {
          const elapsed = performance.now() - startTime;
          if (elapsed > 1000) {
            logger.warn('Slow chunk load', { elapsed: `${elapsed.toFixed(0)}ms`, attempt: attempt + 1 });
          }
        }

        return module;
      } catch (error) {
        lastError = error as Error;

        // Only log errors in development
        if (import.meta.env.DEV) {
          logger.warn(`Lazy load attempt ${attempt + 1} failed`, { error });
        }

        // Don't retry on the last attempt
        if (attempt < maxRetries - 1) {
          // Quick exponential backoff
          const waitTime = delay * Math.pow(1.5, attempt);
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
      }
    }

    // Log final failure
    logger.error('All lazy load attempts failed', {
      error: lastError,
      maxRetries,
    });

    // A failed dynamic import in production almost always means a new deployment
    // shipped and the currently-loaded index.html references a chunk hash that no
    // longer exists (404). Reload ONCE to fetch the fresh index + chunks, guarded
    // against reload loops so a genuinely broken chunk still surfaces an error.
    const msg = (lastError?.message || '').toLowerCase();
    const isChunkError =
      msg.includes('dynamically imported module') ||
      msg.includes('importing a module script failed') ||
      msg.includes('loading chunk') ||
      msg.includes('failed to fetch') ||
      msg.includes('module load timeout') ||
      msg.includes('does not have a default export');
    if (import.meta.env.PROD && isChunkError && typeof window !== 'undefined') {
      try {
        const KEY = 'hs-chunk-reload-ts';
        const last = Number(window.sessionStorage.getItem(KEY) || '0');
        if (Date.now() - last > 10000) {
          window.sessionStorage.setItem(KEY, String(Date.now()));
          window.location.reload();
          // Suspend forever; the reload replaces this document.
          return new Promise<never>(() => {});
        }
      } catch {
        // sessionStorage unavailable (private mode) — fall through to the error UI.
      }
    }

    // Create user-friendly error message for production
    const friendlyError = new Error(
      import.meta.env.PROD
        ? 'Failed to load page. Please refresh your browser.'
        : `Failed to load component after ${maxRetries} attempts: ${lastError?.message}`
    );

    // Attach original error for debugging
    (friendlyError as Error & { originalError?: Error }).originalError = lastError;

    throw friendlyError;
  });
}

import { SkeletonPage } from '@/components/ui/skeleton';

/**
 * Standard loading fallback component - uses skeleton for better perceived performance
 */
export function LoadingState({ message = "Loading..." }: { message?: string }): ReactNode {
  return <SkeletonPage showHeader={true} />;
}

/**
 * Minimal loading state for inline content
 */
export function LoadingSpinner(): ReactNode {
  return (
    <div className="flex items-center justify-center p-8">
      <div className="size-8 animate-spin rounded-full border-4 border-muted border-t-primary" />
    </div>
  );
}
